import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { DepartamentoReadingFiltersDtoProps } from "api/DTOs/DepartamentoReadingFiltersDto"
import {
  GetDepartamentoByFiltersParamsProps,
  deleteDepartamentoById,
} from "api/Departamento.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import notification from "common/utils/notification"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { Dispatch, SetStateAction, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import DepartamentosFilterMenu from "../components/FilterMenuDepartamentos"
import DepartamentosModal from "../components/ModalDepartamentos"
import DepartamentosModalDelete from "../components/ModalDepartamentosDelete"
import DEPARTAMENTOS_COLUMNS from "../components/TableDepartamentos/DepartamentosColumns"
import "../style.scss"

export interface ModalDepartamentosProps {
  isOpen: boolean
  rowData: DepartamentoReadingFiltersDtoProps | null
}
interface DepartamentosProps {
  items: Array<DepartamentoReadingFiltersDtoProps>
  isLoading: boolean
  totalCount: number
  setServiceQuery: Dispatch<SetStateAction<GetDepartamentoByFiltersParamsProps>>
  fetchTableData: () => Promise<void>
}
const Departamentos = ({
  items,
  isLoading,
  totalCount,
  setServiceQuery,
  fetchTableData,
}: DepartamentosProps) => {
  const { t } = useTranslation()
  const [departamentoModalIsOpen, setDepartamentoModalIsOpen] =
    useState<ModalDepartamentosProps>({
      isOpen: false,
      rowData: null,
    })
  const [departamentoModalDeleteIsOpen, setDepartamentoModalDeleteIsOpen] =
    useState("")
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    property: undefined,
    sort: null,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 10,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const dtRef = useRef<DataTablePrimeReact<
    DepartamentoReadingFiltersDtoProps[]
  > | null>(null)
  const closeDepartamentoModal = () =>
    setDepartamentoModalIsOpen({
      rowData: null,
      isOpen: false,
    })
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }

  return (
    <section className='tw-bg-white tw-h-1/2'>
      <DepartamentosFilterMenu
        isLoading={isLoading}
        serviceQuery={setServiceQuery}
        sortingCriteria={sortingCriteria}
        maxResultCount={pagination.itemsPerPage}
        skipCount={pagination.skipCount}
        resetPagination={handleResetPagination}
        openDepartamenteModal={() =>
          setDepartamentoModalIsOpen({ rowData: null, isOpen: true })
        }
        totalCount={totalCount}
      />

      <div className='tw-flex tw-flex-col tw-h-[calc(100%-88px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={DEPARTAMENTOS_COLUMNS(
              t,
              setDepartamentoModalIsOpen,
              setDepartamentoModalDeleteIsOpen
            )}
            className='tw-h-full'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            dataKey='id'
            lazy
            scrollable
            size='small'
            loading={isLoading}
            emptyMessage={t("primeReact.table.emptyMessage")}
          />
          <PaginationERP
            className={PAGINATION_STYLES}
            totalRecords={totalCount}
            first={pagination.skipCount}
            rows={pagination.itemsPerPage}
            resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
            setPagination={setPagination}
            optionDropdown={[10, 20, 50, 100]}
            texts={{
              textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
              textCurrentPageReport: `${t("pagination.from")}`,
            }}
          />
        </div>
      </div>

      <DepartamentosModal
        isOpen={departamentoModalIsOpen?.isOpen}
        close={closeDepartamentoModal}
        refresh={() => {
          closeDepartamentoModal()
          fetchTableData()
        }}
        rowData={
          departamentoModalIsOpen?.rowData as DepartamentoReadingFiltersDtoProps
        }
      />
      <DepartamentosModalDelete
        t={t}
        isOpen={Boolean(departamentoModalDeleteIsOpen)}
        onCancel={() => setDepartamentoModalDeleteIsOpen("")}
        onConfirm={async () => {
          await deleteDepartamentoById(departamentoModalDeleteIsOpen).then(() =>
            notification.success()
          )
          setDepartamentoModalDeleteIsOpen("")
          fetchTableData()
        }}
      />
    </section>
  )
}

export default Departamentos
